/**
 * The structured formula builder's model (#60).
 *
 * Requirements: REQ-FX-014, REQ-FX-015.
 *
 * A formula here is a **flat chain**: one operand, then any number of
 * (operator, operand) pairs. There is no grouping control and no free text, so
 * the set of expressions this file can emit is small enough to reason about —
 * which is what makes REQ-FX-014's "the builder cannot produce an expression
 * that fails validation" a property of the shape rather than a promise.
 *
 * Spec 060 §3.1.1 records what the picker deliberately leaves out: parentheses,
 * `%`, `**`, and arbitrary constants. All of them stay in the grammar and are
 * reachable through free text (#44); none of them belongs on a phone.
 *
 * The server is still the authority. Nothing here validates anything — it
 * builds a string, the API parses it through the full allowlist on every
 * request (REQ-FX-012), and a formula this file could somehow get wrong comes
 * back as a 422 rather than as a wrong number.
 */
import type { Attribute, Registry } from "./api";

/** The four operators the picker offers. Spec 060 §3.1.1 says why not six. */
export const BUILDER_OPERATORS = ["+", "-", "*", "/"] as const;
export type BuilderOperator = (typeof BUILDER_OPERATORS)[number];

/** How each reads on screen. `*` and `/` are the signs people write by hand. */
export const OPERATOR_LABEL: Record<BuilderOperator, string> = {
  "+": "+",
  "-": "−",
  "*": "×",
  "/": "÷",
};

/** Spoken form, for the name a chain proposes for itself. */
const OPERATOR_WORD: Record<BuilderOperator, string> = {
  "+": "plus",
  "-": "minus",
  "*": "times",
  "/": "per",
};

/**
 * The constants offered, because a keyboard is what this flow exists to avoid.
 *
 * Chosen for what actually turns up in the spec's own examples: 1000 converts
 * mm³ to cm³, 10 and 100 rescale a ratio into a readable range, and the
 * fractions halve or third a term. An arbitrary constant is a free-text
 * formula (spec 060 §3.1.1).
 */
export const BUILDER_NUMBERS = [0.5, 2, 3, 10, 100, 1000] as const;

export type Operand =
  | { kind: "column"; key: string }
  | { kind: "number"; value: number };

/** One link in the chain: the operator that joins it to what came before. */
export interface Term {
  op: BuilderOperator;
  /** null until the person picks one: "+ Add a spec" must not choose for them. */
  operand: Operand | null;
}

export interface Draft {
  first: Operand | null;
  rest: Term[];
}

export const EMPTY_DRAFT: Draft = { first: null, rest: [] };

/** A chain is usable once it has at least one operator and both its operands. */
export function isComplete(d: Draft): boolean {
  return d.first !== null && d.rest.length > 0 && d.rest.every((t) => t.operand !== null);
}

function operandSource(o: Operand): string {
  // `String(n)` and not `toFixed`: 0.5 must emit "0.5", never "0.50", and the
  // list is small and all exactly representable.
  return o.kind === "column" ? o.key : String(o.value);
}

/**
 * The formula the API receives.
 *
 * Spaces around operators so a 422's character offset points somewhere a
 * person can find (REQ-FX-013), which matters even though this path should
 * never produce one.
 */
export function toFormula(d: Draft): string {
  if (!d.first) return "";
  let out = operandSource(d.first);
  for (const t of d.rest) if (t.operand) out += ` ${t.op} ${operandSource(t.operand)}`;
  return out;
}

/**
 * Where the multiplicative runs are, so the display can bracket them.
 *
 * Returns one group per additive term: each is a list of positions into the
 * flattened operand sequence. `a + b * c` gives [[0], [1, 2]].
 */
function multiplicativeRuns(d: Draft): number[][] {
  const groups: number[][] = [[0]];
  d.rest.forEach((t, i) => {
    const at = i + 1;
    if (t.op === "*" || t.op === "/") groups[groups.length - 1]!.push(at);
    else groups.push([at]);
  });
  return groups;
}

/**
 * The expression as the reader should see it, bracketed the way the server
 * will group it (REQ-FX-015).
 *
 * "Honours precedence" is not enough on its own: `weight + length × diameter`
 * is unambiguous to the parser and ambiguous to a person reading it on a
 * phone. Showing `weight + (length × diameter)` is the difference between
 * obeying the rule and communicating it.
 */
export function toDisplay(
  d: Draft,
  label: (o: Operand) => string,
): string {
  if (!d.first) return "";
  // Only meaningful for a complete chain; an unpicked operand reads as blank.
  const operands = [d.first, ...d.rest.map((t) => t.operand)];
  const ops = d.rest.map((t) => t.op);
  const runs = multiplicativeRuns(d);

  const rendered = runs.map((run) => {
    const parts = run.map((at, i) => {
      const o = operands[at];
      const text = o ? label(o) : "…";
      // ops[at - 1] is the operator that introduced this operand.
      return i === 0 ? text : `${OPERATOR_LABEL[ops[at - 1]!]} ${text}`;
    });
    const joined = parts.join(" ");
    // Brackets only where they say something: a lone term needs none, and a
    // formula that is entirely one product needs none either.
    return run.length > 1 && runs.length > 1 ? `(${joined})` : joined;
  });

  let out = rendered[0]!;
  for (let i = 1; i < runs.length; i++) {
    const joinAt = runs[i]![0]!;
    out += ` ${OPERATOR_LABEL[ops[joinAt - 1]!]} ${rendered[i]}`;
  }
  return out;
}

/**
 * The name the chain proposes for itself (spec 060 §3.1.1).
 *
 * Derived rather than typed, because a text field at the end of a
 * never-typed flow fails REQ-FX-014's acceptance as surely as one at the
 * start. Collisions are resolved by the caller against what already exists.
 */
/**
 * A computed column's name read as a person would say it.
 *
 * The name is a machine key by necessity — the API's rule is lower case,
 * letters, digits and underscores — and until 2026-09-10 it was also used as
 * the label, so a card, the sort menu and the column picker all showed
 * `LENGTH_MM_TIMES_DIAMETER_MM` (upper-cased by the header style) beside a
 * number. Nothing about that tells a reader what they are looking at.
 *
 * This changes only what is displayed. The name stays the key, so a shared URL
 * keeps working (D7: the URL is the query).
 */
/**
 * Labels for the current query's computed columns, read from their formulas
 * (#218). Set by the app from the query and the registry before it renders
 * anything that shows a label, so every label site, the cards, the sort
 * headline, the pickers and the CSV header, agrees without each needing the
 * formula passed down to it. Derived data only: rebuilt from the query each
 * render, never the source of anything.
 */
const computedLabels = new Map<string, string>();

export function setComputedLabels(
  computed: readonly { name: string; formula: string }[],
  labelOfKey: (key: string) => string,
): void {
  computedLabels.clear();
  for (const c of computed) computedLabels.set(c.name, formulaLabel(c.formula, labelOfKey));
}

export function humaniseComputed(name: string): string {
  const known = computedLabels.get(name);
  if (known) return known;
  const words: Record<string, string> = {
    plus: "+", minus: "−", times: "×", per: "÷",
  };
  const text = name
    .split("_")
    // `_mm`, `_g`, `_deg` are unit suffixes on the source column and say
    // nothing about the derived quantity, which has no unit at all.
    .filter((w, i, all) => !(i > 0 && ["mm", "g", "deg", "m"].includes(w) && all.length > 2))
    .map((w) => words[w] ?? w)
    .join(" ")
    .trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A computed column's label, read from its formula (#218).
 *
 * The label used to be the column's *name* read aloud, and the name is cut to
 * 48 characters, so a long formula was labelled mid-expression —
 * "Weight ÷ length × diameter −" — on the card, the sort menu and the CSV
 * header, and the grouping the builder showed was lost once it was built.
 *
 * A formula the builder made is a flat chain, so it is read back into a Draft
 * and shown by the builder's own toDisplay: the same brackets the preview
 * showed. Anything else (a hand-written formula from a shared link, with its
 * own brackets or `**`) is shown as written, with readable symbols and names.
 */
export function formulaLabel(formula: string, labelOfKey: (key: string) => string): string {
  const draft = parseChain(formula);
  if (draft) {
    return toDisplay(draft, (o) => (o.kind === "number" ? String(o.value) : labelOfKey(o.key)));
  }
  // Symbols first, names second: a spec's own name may contain a hyphen,
  // which must not become a minus sign.
  return formula
    .replace(/\*\*/g, "^")
    .replace(/\*/g, "×")
    .replace(/\//g, "÷")
    .replace(/-/g, "−")
    .replace(/[a-z][a-z0-9_]*/g, (w) => labelOfKey(w))
    .replace(/\s+/g, " ")
    .trim();
}

/** A flat chain as toFormula writes it, or null if the text is anything else. */
function parseChain(formula: string): Draft | null {
  const tokens = formula.trim().split(/\s+/);
  if (tokens.length < 3 || tokens.length % 2 === 0) return null;
  const operand = (t: string): Operand | null =>
    /^[a-z][a-z0-9_]*$/.test(t)
      ? { kind: "column", key: t }
      : /^\d+(\.\d+)?$/.test(t)
        ? { kind: "number", value: Number(t) }
        : null;
  const first = operand(tokens[0]!);
  if (!first) return null;
  const rest: Term[] = [];
  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i] as BuilderOperator;
    const o = operand(tokens[i + 1]!);
    if (!BUILDER_OPERATORS.includes(op) || !o) return null;
    rest.push({ op, operand: o });
  }
  return { first, rest };
}

export function suggestName(d: Draft): string {
  if (!d.first) return "computed";
  const part = (o: Operand) =>
    o.kind === "column" ? o.key : String(o.value).replace(".", "p");
  // The API's rule: lower case, letters, digits and underscores, leading letter.
  const clean = (x: string) => x.toLowerCase().replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_");
  let out = clean(part(d.first));
  // The name is only a key now (the label comes from the formula, #218), but a
  // key read in a URL should still not end on "_minus". Whole terms only, up
  // to 48 characters.
  for (const t of d.rest) {
    if (!t.operand) break;
    const next =`${out}_${clean(`${OPERATOR_WORD[t.op]}_${part(t.operand)}`)}`;
    if (next.length > 48) break;
    out = next;
  }
  if (!/^[a-z]/.test(out)) out = `c_${out}`;
  return out;
}

/** `density`, `density_2`, `density_3` — never a silent overwrite. */
export function uniqueName(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  for (let n = 2; n < 100; n++) {
    const candidate = `${base}_${n}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}_${Date.now()}`;
}

/**
 * The attributes a formula may reference.
 *
 * Numeric only, matching what the API accepts: a formula pointing at a text
 * column is refused where it is defined rather than nulled for every row
 * (spec 060 §3.2). Read from the registry, never from a list of keys written
 * here — an attribute added by INSERT must not need a frontend change
 * (REQ-DM-002).
 */
export function numericAttributes(registry: Registry | null): Attribute[] {
  return (registry?.attributes ?? []).filter(
    (a) => a.datatype === "numeric" || a.datatype === "integer",
  );
}

export interface Preset {
  label: string;
  /** Plain words after the formula, or "" when the formula says it all (#213). */
  note: string;
  draft: Draft;
}

/**
 * Starting points, so the first encounter with the feature is not a blank row.
 *
 * Taken from spec 060 §2's own worked examples rather than invented here, and
 * every one is expressible as a flat chain: the density example is written
 * `weight ÷ length ÷ diameter`, which is the same number as
 * `weight / (length × diameter)` without needing a bracket the picker does not
 * have.
 *
 * A preset whose columns are missing from the registry is dropped rather than
 * offered — tapping a suggestion and getting an empty column teaches people the
 * feature is broken.
 */
export function presets(registry: Registry | null): Preset[] {
  const have = new Set(numericAttributes(registry).map((a) => a.key));
  const col = (key: string): Operand => ({ kind: "column", key });
  const num = (value: number): Operand => ({ kind: "number", value });

  const all: Preset[] = [
    {
      label: "Size index",
      note: "roughly how much bag it takes",
      draft: {
        first: col("length_mm"),
        rest: [{ op: "*", operand: col("diameter_mm") }],
      },
    },
    {
      label: "Weight per mm",
      note: "how heavy it is for its size",
      draft: {
        first: col("weight_g"),
        rest: [{ op: "/", operand: col("length_mm") }],
      },
    },
    {
      label: "Volume",
      note: "",
      draft: {
        first: col("length_mm"),
        rest: [
          { op: "*", operand: col("diameter_mm") },
          { op: "*", operand: col("diameter_mm") },
          { op: "/", operand: num(1000) },
        ],
      },
    },
    {
      label: "Density",
      note: "",
      draft: {
        first: col("weight_g"),
        rest: [
          { op: "/", operand: col("length_mm") },
          { op: "/", operand: col("diameter_mm") },
        ],
      },
    },
  ];

  const usable = (d: Draft) =>
    [d.first, ...d.rest.map((t) => t.operand)].every(
      (o) => o !== null && (o.kind === "number" || have.has(o.key)),
    );
  return all.filter((p) => usable(p.draft));
}
